import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { WINDOW } from '@ng-web-apis/common';
import { Segment } from '@types';
import {
  ENDPOINT,
  EXTERNAL,
  LOGOUT_TRUE,
  LOGOUT_TYPE_M,
  ROLE_REDIRECT,
  SEGMENT_TYPE,
  USER_GROUP,
  USER_TYPE_COOKIE,
} from '@utils/app.constants';
import { Logger } from '@utils/logger';
import { Observable, of } from 'rxjs';
import { map, switchMap, tap, timeout } from 'rxjs/operators';
import { AppStateService } from './app-state.service';
import { IUserProfileInfo } from './profile.interface';
import { StorageService } from './storage.service';
import { ServerCookieService } from '@services/server-cookie.service';

const logger = Logger.getLogger('SessionService');

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache',
    Pragma: 'no-cache',
    'If-Modified-Since': '0',
  }),
  withCredentials: true,
  observe: 'response' as 'response',
};

// codes returned from accounts api calls
export enum SessionCodes {
  NOT_LOGGED_IN = 'PROFINTL100138',
  // TODO: add more codes as required
}

@Injectable({
  providedIn: 'root',
})
export class SessionService {
  renewTimeout: number;
  constructor(
    private http: HttpClient,
    private appStateService: AppStateService,
    @Inject(WINDOW) readonly windowRef: Window,
    private storageService: StorageService,
    private serverCookieService: ServerCookieService
  ) {}

  /**
   * Function to validate Token
   */
  public validateSession$(
    requestTimeout: number
  ): Observable<IUserProfileInfo> {
    const validateTokenUrl = `${this.appStateService.getAccountsApiUrl()}${
      ENDPOINT.validateSession.link
    }`;
    logger.debug('Triggering Validate Session API');
    return this.http.post(validateTokenUrl, {}, httpOptions).pipe(
      timeout(requestTimeout),
      switchMap((sessionData: any) => {
        logger.debug(
          'Session Service - validateSession - Success',
          sessionData
        );
        let user = sessionData.body?.result as IUserProfileInfo; // try myfunds response first
        if (!user) {
          user = sessionData.body;
        }
        if (user?.userId && user.userSysNo) {
          // logged in US user
          this.storageService.store<string>(USER_GROUP, user.userGroup, true);
          // valid token
          // need to get full profile data
          return this.getProfileDetails$();
        }
        return of(user).pipe(
          map((userProfile) => {
            if (userProfile?.userSysNo) {
              // logged in myfunds user
              userProfile.userId = userProfile.uid; // map myfunds response to match us accounts
              if (this.appStateService.channel === 'de-de') {
                userProfile.role =
                  userProfile.userGroup === 'INVESTOR'
                    ? 'INVESTOR'
                    : 'distributor';
                userProfile.webExperience =
                  userProfile.userGroup === 'INVESTOR'
                    ? 'investor'
                    : 'distributor';
              } else {
                userProfile.role = userProfile.userGroup;
                userProfile.webExperience =
                  userProfile.userGroup === 'INVESTOR' ? 'investor' : 'FA';
              }
            }
            return userProfile;
          })
        );
      })
    );
  }

  /**
   * Function to get the profile details
   */
  private getProfileDetails$(): Observable<any> {
    const userDetailUrl = `${this.appStateService.getAccountsApiUrl()}${
      ENDPOINT.getProfile.link
    }`;
    logger.debug('Triggering Profile Data API');
    return this.http.get(userDetailUrl, httpOptions).pipe(
      timeout(7000),
      map((res) => res.body),
      tap((profileInfo) =>
        this.setRenewTokenTimer(profileInfo.expiryTimeInSeconds)
      )
    );
  }

  /**
   * calls the authentication API to authenticate user
   * @param payload string: credentials are sent as a single string as payload for the POST API
   */
  public authenticateUser$(payload: any): Observable<any> {
    const loginHttpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded',
      }),
      withCredentials: true,
      observe: 'response' as 'response', // Required to pass the location back
    };
    return this.http.post(this.getLoginUrl(), payload, loginHttpOptions);
  }

  public getLoginUrl(): string {
    switch (this.appStateService.getAuthenticationType()) {
      case 'myfunds':
        return `${this.appStateService.getAccountsApiUrl()}/login`;
      case 'canada':
        return `${this.appStateService.getAccountsApiUrl()}`;
      default:
        // us
        return `${this.appStateService.getAccountsApiUrl()}${
          ENDPOINT.authenticate.link
        }`;
    }
  }

  private setRenewTokenTimer(expiryTimeInSeconds: number) {
    if (expiryTimeInSeconds) {
      const renewPath =
        this.appStateService.getAuthenticationType() === 'myfunds'
          ? 'renew'
          : 'renewal';
      const renewTokenUrl = `${this.appStateService.getAccountsApiUrl()}${renewPath}`;

      this.renewTimeout = window.setTimeout(() => {
        logger.debug('extending auth token');
        this.http.get(renewTokenUrl).subscribe(
          () => {
            logger.debug('successfully renewed');
            // TODO need to get the new expiry time.
            return true;
          },
          () => {
            logger.error('error when trying to renew');
            return false;
          }
        );
      }, Number(expiryTimeInSeconds) * 1000 - 30000); // renew 30 seconds before the token expires.
    }
  }

  public logout(segment?: Segment) {
    if (this.renewTimeout) {
      clearTimeout(this.renewTimeout);
    }
    logger.debug('Entering logout', segment);
    switch (this.appStateService.getAuthenticationType()) {
      case 'myfunds':
        logger.debug('Triggering logout for MyFunds', segment);
        this.http
          .delete(
            `${this.appStateService.getAccountsApiUrl()}/logout`,
            httpOptions
          )
          .subscribe(
            () => {
              //   this.windowRef.location.href = `${this.appStateService.getHomePageUrl()}?${LOGOUT_TRUE}${LOGOUT_TYPE_M}`;
              // Is segment available in logout url, need to be check and apply the segment.
              if (segment) {
                // Is selected segment is externel one need to be redirect
                if (segment?.externalLink) {
                  logger.debug(
                    'redirect to segment external link',
                    segment.externalLink
                  );
                  this.windowRef.location.href = `${this.appStateService.getHomePageUrl()}?${LOGOUT_TRUE}${LOGOUT_TYPE_M}&${ROLE_REDIRECT}${
                    segment.id
                  }&${SEGMENT_TYPE}${EXTERNAL}`;
                } else {
                  // set the selected segment in logout url
                  this.windowRef.location.href = `${this.appStateService.getHomePageUrl()}?${LOGOUT_TRUE}${LOGOUT_TYPE_M}&${ROLE_REDIRECT}${
                    segment.id
                  }`;
                }
              } else {
                // Is segment not available in logout url, need to be set normal logout url
                this.windowRef.location.href = `${this.appStateService.getHomePageUrl()}?${LOGOUT_TRUE}${LOGOUT_TYPE_M}`;
              }
            },
            (e) => {
              logger.error('Unable to log out', e);
            }
          );
        break;

      case 'canada':
        logger.debug('Triggering logout for Canada');
        // Set checkCookie to allow remove not only local-storage cookies.
        this.storageService.setCheckCookie(true);
        // Set empty cookie value to avoid get cookie form browser cache (for HttpOnly cookie)
        // this.serverCookieService.set(USER_TYPE_COOKIE, { cookieValue: '' });
        // Remove userType cookie before redirect to Canada accounts sign out.
        this.storageService.remove(USER_TYPE_COOKIE, false, USER_TYPE_COOKIE);
        this.storageService.remove(
          'user_firm_channel',
          false,
          'user_firm_channel'
        );
        // Sett segment cookie for Canada language channels
        for (const channel of ['en-ca', 'fr-ca']) {
          this.storageService.remove(`isLoggedIn_${channel}`, true);
          this.storageService.remove(`profile_${channel}`);
          this.setSegmentOnSignOut(channel, segment);
        }
        this.storageService.setCheckCookie(false);
        // Go to accounts sign out API to finish sign-out process.
        this.windowRef.location.href = this.appStateService.getCanadaSignOutApiURL();
        break;

      default:
        // us
        // Is segment available in logout url, need to be check and apply the segment.
        logger.debug('Triggering logout for US');
        if (segment) {
          // Is selected segment is externel one need to be redirect
          if (segment?.externalLink) {
            logger.debug(
              'redirect to segment external link',
              segment.externalLink
            );
            this.windowRef.location.href = `${this.appStateService.getAccountsApiUrl()}${
              ENDPOINT.signOut.link
            }${LOGOUT_TYPE_M}&${ROLE_REDIRECT}${
              segment.id
            }&${SEGMENT_TYPE}${EXTERNAL}`;
          } else {
            // set the selected segment in logout url
            this.windowRef.location.href = `${this.appStateService.getAccountsApiUrl()}${
              ENDPOINT.signOut.link
            }${LOGOUT_TYPE_M}&${ROLE_REDIRECT}${segment.id}`;
          }
        } else {
          // Is segment not available in logout url, need to be set normal logout url
          this.windowRef.location.href = `${this.appStateService.getAccountsApiUrl()}${
            ENDPOINT.signOut.link
          }${LOGOUT_TYPE_M}`;
        }
    }
  }

  /**
   * Set segment during SignOut
   * @param channel - site channel string
   * @param segment - selected segment string
   */
  private setSegmentOnSignOut(channel: string, segment: Segment): void {
    if (!segment) {
      this.storageService.retrieve(`segment_${channel}`).then((segmentId) => {
        // Set segment to FP if it is internal
        if (segmentId === 'internal') {
          this.storageService.store(
            `segment_${channel}`,
            'financial-professionals',
            false,
            `segment_${channel}`
          );
        }
      });
    } else {
      this.storageService.store(
        `segment_${channel}`,
        segment.id,
        false,
        `segment_${channel}`
      );
    }
  }
}
